/**
 * The roadworthiness inspection a vehicle has to pass every so many months (HU, MOT …). A vehicle
 * keeps the date the next one is due. A garage enters it as a month (like the German plate
 * sticker) or as an exact date (like a UK MOT certificate); in month mode the first day of the
 * month is stored and the inspection is due by the end of that month.
 */

export const ROADWORTHINESS_DUE_AS = ["month", "date"] as const;
export type RoadworthinessDueAs = (typeof ROADWORTHINESS_DUE_AS)[number];

/** How long before the due date a vehicle counts as due soon. */
export const ROADWORTHINESS_DUE_SOON_MONTHS = 2;

export const ROADWORTHINESS_STATUSES = ["ok", "dueSoon", "overdue"] as const;
export type RoadworthinessStatus = (typeof ROADWORTHINESS_STATUSES)[number];

function parts(date: string): [number, number, number] {
  return date.split("-").map(Number) as [number, number, number];
}

function iso(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** `date` ("YYYY-MM-DD") plus whole months; a day the target month lacks becomes its last day. */
export function addMonths(date: string, months: number): string {
  const [y, m, d] = parts(date);
  const index = y * 12 + (m - 1) + months;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return iso(year, month, Math.min(d, daysInMonth(year, month)));
}

/** The first day of the month `date` is in. */
export function firstOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

/** The last day the inspection may be done: the stored date, or in month mode its month's end. */
export function roadworthinessDeadline(dueOn: string, dueAs: RoadworthinessDueAs): string {
  if (dueAs === "date") return dueOn;
  const [y, m] = parts(dueOn);
  return iso(y, m, daysInMonth(y, m));
}

/**
 * Overdue once `today` is past the deadline; due soon from `ROADWORTHINESS_DUE_SOON_MONTHS`
 * before the due date (in month mode, from the first of that earlier month).
 */
export function roadworthinessStatus(
  dueOn: string,
  dueAs: RoadworthinessDueAs,
  today: string,
): RoadworthinessStatus {
  if (today > roadworthinessDeadline(dueOn, dueAs)) return "overdue";
  const start = dueAs === "month" ? firstOfMonth(dueOn) : dueOn;
  return today >= addMonths(start, -ROADWORTHINESS_DUE_SOON_MONTHS) ? "dueSoon" : "ok";
}

/**
 * When the next inspection is due after one passed on `completedOn`: the interval counts from the
 * month of the inspection (month mode) or from its day.
 */
export function nextRoadworthinessDue(
  completedOn: string,
  intervalMonths: number,
  dueAs: RoadworthinessDueAs,
): string {
  return dueAs === "month"
    ? addMonths(firstOfMonth(completedOn), intervalMonths)
    : addMonths(completedOn, intervalMonths);
}
